import styled from 'styled-components';
import { Box, Icon, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import NameCell from '../Table/NameCell';
import i18n from '../../i18n';
import StatusSwitch from './StatusSwitch';
import { coachTypesDescriptionMapper, coachTypesMapper } from '../../utils/utils';
import { SubscriptionPlanTypes } from '../../types/coach';
import { formatPlanName } from '../../utils/paymentUtills';

const TooltipItem = ({ color, label }: { color: string; label: string }) => (
  <Box display="flex" gap="8px" justifyContent="flex-start" alignItems="center">
    <Box width="16px" height="16px" bgcolor={color} borderRadius="2px" />
    <Typography variant="meta" color="#fff">
      {label}
    </Typography>
  </Box>
);

const CustomTooltipContent = () => (
  <Box p="8px" display="flex" flexDirection="column" gap="6px">
    <TooltipItem color="primary.main" label="Good Engagement" />
    <TooltipItem color="rgba(0, 70, 67, 0.65)" label="Average Engagement" />
    <TooltipItem color="rgba(0, 70, 67, 0.35)" label="Low Engagement" />
    <TooltipItem color="rgba(0, 70, 67, 0.05)" label="No Data" />
  </Box>
);

const StyledTypography = styled(Typography)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: block;
`;

const MobileNumber = (text: string) => {
  // use mobile number formatter
  if (!text) return '-';
  return text;
};

const StyledTooltip = ({
  children,
  text,
  textTransform,
}: {
  children: React.ReactElement;
  text: string;
  textTransform?: boolean;
}) => (
  <Tooltip
    title={text}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: '#2A3C67',
          color: 'white',
          textTransform: textTransform ? 'capitalize' : 'none',
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

export interface ICoachRow {
  id: string;
  avatar: string;
  name: string;
  firstName: string;
  lastName: string;
  profilePic: string;
  activeClients: number;
  profilePicture: string;
  status: string;
  isActive: boolean;
  archivedClients: number;
  inactiveClients: number;
  email: string;
  brandTag: string | null;
  tp: string;
  lastSeenTime: string;
  subscriptionPlan: SubscriptionPlanTypes;
  coachTypes: string[] | null;
  therapyTypes: string[] | null;
}

interface IRenderCellParams {
  row: ICoachRow;
  value: string | boolean;
}

export interface IColumn {
  field: string;
  headerName: string;
  hide?: boolean;
  width?: number;
  disableColumnMenu?: boolean;
  sortable?: boolean;
  flex?: number;
  renderCell?: (_params: IRenderCellParams) => React.ReactNode;
  renderHeader?: (_params: IRenderCellParams) => React.ReactNode;
}
const columns: IColumn[] | undefined = [
  { field: 'id', headerName: 'ID', hide: true },
  {
    field: 'name',
    headerName: i18n.t('coachesTable.name'),
    width: 500,
    disableColumnMenu: true,
    sortable: false,
    flex: 2,
    renderCell: (params: IRenderCellParams) => (
      <StyledTooltip text={`${params.row.firstName} ${params.row.lastName}`}>
        <StyledTypography variant="bodyRegular">
          <NameCell
            avatar={params.row.profilePic}
            firstName={params.row.firstName}
            lastName={params.row.lastName}
            brandTag={params.row.brandTag}
          />
        </StyledTypography>
      </StyledTooltip>
    ),
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta">{i18n.t('coachesTable.name')}</Typography>
      </Box>
    ),
  },
  {
    field: 'activeClients',
    headerName: i18n.t('common.modules.activeClients'),
    width: 150,
    disableColumnMenu: true,
    sortable: false,
    flex: 1,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta">{i18n.t('common.modules.activeClients')}</Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <Typography variant="bodyRegular">{params.row.activeClients || 0}</Typography>
    ),
  },
  {
    field: 'inactiveClients',
    headerName: i18n.t('coachesTable.inactiveClients'),
    width: 250,
    disableColumnMenu: true,
    sortable: false,
    flex: 1.2,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta">{i18n.t('coachesTable.inactiveClients')}</Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <Typography variant="bodyRegular">{params.row.inactiveClients || 0}</Typography>
    ),
  },
  {
    field: 'archivedClients',
    headerName: i18n.t('coachesTable.archived'),
    width: 200,
    disableColumnMenu: true,
    sortable: false,
    flex: 1,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta">{i18n.t('coachesTable.archived')}</Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <Typography variant="bodyRegular">{params.row.archivedClients || 0}</Typography>
    ),
  },
  {
    field: 'subscription',
    headerName: i18n.t('coachesTable.subscription'),
    width: 100,
    disableColumnMenu: true,
    sortable: false,
    flex: 1,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta">{i18n.t('coachesTable.subscription')}</Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <StyledTooltip text={formatPlanName(params.row.subscriptionPlan)} textTransform>
        <StyledTypography variant="bodyRegular" sx={{ textTransform: 'capitalize' }}>
          {formatPlanName(params.row.subscriptionPlan)}
        </StyledTypography>
      </StyledTooltip>
    ),
  },
  {
    field: 'coachTypes',
    headerName: i18n.t('coachesTable.coachingType'),
    width: 300,
    disableColumnMenu: true,
    sortable: false,
    flex: 1.2,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta">{i18n.t('coachesTable.coachingType')}</Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <StyledTooltip text={coachTypesMapper(params.row.coachTypes)}>
        <StyledTypography variant="bodyRegular">
          {coachTypesMapper(params.row.coachTypes)}
        </StyledTypography>
      </StyledTooltip>
    ),
  },
  {
    field: 'therapyTypes',
    headerName: i18n.t('coachesTable.therapyTypes'),
    width: 350,
    disableColumnMenu: true,
    sortable: false,
    flex: 1.3,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta">{i18n.t('coachesTable.therapyTypes')}</Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <StyledTooltip text={coachTypesDescriptionMapper(params.row.therapyTypes)}>
        <StyledTypography variant="bodyRegular">
          {coachTypesDescriptionMapper(params.row.therapyTypes)}
        </StyledTypography>
      </StyledTooltip>
    ),
  },
  {
    field: 'lastSeenTime',
    headerName: i18n.t('coachesTable.lastlogintime'),
    disableColumnMenu: true,
    width: 280,
    sortable: false,
    flex: 1.2,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary.main">
          {i18n.t('coachesTable.lastlogintime')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <StyledTooltip text={dayjs(params.row.lastSeenTime).format('YYYY/MM/DD - HH:mm:ss A')}>
        <StyledTypography variant="bodyRegular">
          {dayjs(params.row.lastSeenTime).format('YYYY/MM/DD - HH:mm:ss A') || '-'}
        </StyledTypography>
      </StyledTooltip>
    ),
  },
  {
    field: 'email',
    headerName: '',
    disableColumnMenu: true,
    width: 40,
    sortable: false,
    flex: 0.6,
    renderCell: (params: IRenderCellParams) => (
      <StyledTooltip text={params.row.email || '-'}>
        <Icon>
          <img src="/assets/icons/letter-coach.svg" alt="letter" />
        </Icon>
      </StyledTooltip>
    ),
  },
  {
    field: 'tp',
    headerName: '',
    disableColumnMenu: true,
    width: 50,
    sortable: false,
    flex: 0.5,
    renderCell: (params: IRenderCellParams) => (
      <StyledTooltip text={params.row.tp && MobileNumber(params.row.tp)}>
        {params.row.tp ? (
          <Icon>
            <img src="/assets/icons/phone-coach.svg" alt="phone" />
          </Icon>
        ) : (
          <Icon />
        )}
      </StyledTooltip>
    ),
  },
];

export const activeCoachesColumns = [
  ...columns.slice(0, 9),
  {
    field: 'status',
    headerName: i18n.t('coachesTable.status'),
    disableColumnMenu: true,
    sortable: false,
    flex: 1,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary.main">
          {i18n.t('coachesTable.status')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <StatusSwitch isActive={params.row.isActive} rowId={params.row.id} />
    ),
  },
  ...columns.slice(9, 11),
  // {
  //   field: 'actions',
  //   headerName: '',
  //   width: 100,
  //   disableColumnMenu: true,
  //   sortable: false,
  //   flex: 1,
  //   renderCell: (params: IRenderCellParams) => <RowActions row={params.row} />,
  // },
];

export const archivedCoachesColumns = [...columns];

export default columns;
